import React, {useCallback, useEffect, useState} from 'react';
import toast from 'react-hot-toast';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faBell, faBellSlash, faMobileScreen, faSpinner} from '@fortawesome/free-solid-svg-icons';
import {useAuth} from '../../contexts/AuthContext';
import {disablePush, enablePush, getPushState, isPushRegistered, PushState} from '../../utils/push';

/**
 * Nút bật thông báo đẩy - dùng chung cho cả trang Cài đặt (chủ nhà) và trang
 * Phòng của tôi (người thuê).
 *
 * Trạng thái là THEO TỪNG MÁY chứ không theo tài khoản: bật trên điện thoại
 * không làm laptop tự bật. Nên chữ trên nút luôn nói rõ "máy này", tránh việc
 * mở laptop thấy chữ "đang tắt" rồi tưởng điện thoại cũng tắt theo.
 */

/** iOS chỉ cho web push khi app đã được cài ra màn hình chính */
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

const isStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as any).standalone === true;

export const PushToggle: React.FC<{ className?: string }> = ({className = ''}) => {
    const {user} = useAuth();
    const email = user?.email ?? null;

    const [state, setState] = useState<PushState | null>(null);
    /* Có dòng trong pushTokens hay không - KHÔNG suy ra từ `state`. Quyền của
       trình duyệt và token trong DB lệch nhau được, xem isPushRegistered. */
    const [registered, setRegistered] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            const next = await getPushState();
            const hasToken = next === 'granted' && !!email && await isPushRegistered(email);
            if (!alive) return;
            setState(next);
            setRegistered(hasToken);
        })();
        return () => {
            alive = false;
        };
    }, [email]);

    const handleEnable = useCallback(async () => {
        if (!email) return;
        setBusy(true);
        try {
            const next = await enablePush(email);
            setState(next);
            setRegistered(next === 'granted');
            if (next === 'granted') toast.success('Đã bật thông báo trên máy này');
            else if (next === 'denied') toast.error('Trình duyệt đã chặn. Mở lại trong cài đặt của máy.');
            else toast('Bạn chưa cho phép hiện thông báo. Hộp thoại bị bỏ qua, hoặc máy đang tắt thông báo của trình duyệt.', {duration: 10000});
        } catch (error) {
            console.error('Error enabling push:', error);
            /* Hiện thẳng mã lỗi ra màn hình thay vì "vui lòng thử lại".
               Trên điện thoại không có chỗ nào mở console, mà lỗi ở bước này gần
               như luôn là một mã cụ thể của FCM (messaging/token-subscribe-failed,
               messaging/failed-service-worker-registration...). Biết mã là biết
               phải sửa gì; "thử lại" thì thử mười lần vẫn vậy. */
            const {code, message} = (error ?? {}) as {code?: string; message?: string};
            toast.error(`Không bật được — ${code || message || String(error)}`, {duration: 15000});
        } finally {
            setBusy(false);
        }
    }, [email]);

    const handleDisable = useCallback(async () => {
        if (!email) return;
        setBusy(true);
        try {
            await disablePush(email);
            // Quyền của trình duyệt vẫn là 'granted' - ta chỉ bỏ token. Đặt lại
            // 'default' để nút quay về "Bật thông báo", đúng với cái người dùng
            // vừa làm.
            setState('default');
            setRegistered(false);
            toast.success('Đã tắt trên máy này');
        } catch (error) {
            console.error('Error disabling push:', error);
            toast.error('Không tắt được. Vui lòng thử lại.');
        } finally {
            setBusy(false);
        }
    }, [email]);

    if (state === null) return null;

    // iOS: chưa cài ra màn hình chính thì có bấm cũng không xin quyền được.
    if (state === 'unsupported' && isIOS() && !isStandalone()) {
        return (
            <div className={`flex items-start gap-3 text-sm text-slate-500 dark:text-slate-400 ${className}`}>
                <FontAwesomeIcon icon={faMobileScreen} className="mt-0.5 flex-shrink-0"/>
                <p>
                    Trên iPhone cần cài app ra màn hình chính trước: bấm nút Chia sẻ trong Safari,
                    chọn <span className="font-medium text-slate-700 dark:text-slate-200">Thêm vào MH chính</span>,
                    rồi mở app từ đó và quay lại đây.
                </p>
            </div>
        );
    }

    // Chưa cấu hình khoá Web Push thì KHÔNG hiện gì cả. Đây là việc của người
    // dựng hệ thống (xem README), người thuê mở app ra mà thấy một dòng báo lỗi
    // họ không sửa được thì chỉ tổ làm họ tưởng app hỏng.
    if (state === 'unconfigured') return null;

    if (state === 'unsupported') {
        return (
            <p className={`text-sm text-slate-500 dark:text-slate-400 ${className}`}>
                Trình duyệt này không nhận được thông báo đẩy.
            </p>
        );
    }

    if (state === 'denied') {
        return (
            <p className={`text-sm text-slate-500 dark:text-slate-400 ${className}`}>
                Bạn đã chặn thông báo cho trang này. Mở lại trong cài đặt trình duyệt hoặc cài đặt máy,
                rồi quay lại đây bật.
            </p>
        );
    }

    /* "Đang bật" = CÓ TOKEN trong DB, không phải chỉ có quyền. Quyền đã cấp mà
       token đăng ký hỏng thì nút phải mời bấm lại, chứ báo "đang bật" là để
       người ta ngồi đợi một thông báo không bao giờ tới. */
    const on = state === 'granted' && registered;
    const permittedButUnregistered = state === 'granted' && !registered;

    return (
        <div className={`flex flex-wrap items-center gap-3 ${className}`}>
            <button
                type="button"
                onClick={on ? handleDisable : handleEnable}
                disabled={busy || !email}
                className="glass-btn inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                    disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <FontAwesomeIcon
                    icon={busy ? faSpinner : on ? faBellSlash : faBell}
                    spin={busy}
                />
                {on ? 'Tắt' : 'Bật thông báo'}
            </button>
            {/* Nút nói VIỆC, dòng này nói ĐƯỢC GÌ - đừng để cả hai cùng nói
                "trên máy này" như trước. Lúc đang tắt thì thứ người ta cần biết
                là bật lên được gì, chứ không phải nhắc lại là đang tắt. */}
            <span className="text-sm text-slate-500 dark:text-slate-400">
                {on ? 'Đang bật trên máy này' : 'Nhận cả khi đã đóng app'}
            </span>
            {permittedButUnregistered && (
                <p className="w-full text-xs text-slate-500 dark:text-slate-400">
                    Chưa đăng ký được với máy chủ - bấm lại giúp.
                </p>
            )}
        </div>
    );
};
